import type {
    QueriesOptions,
    UseQueryOptions,
    UseQueryResult,
    UseSuspenseQueryOptions,
    UseSuspenseQueryResult,
} from "@tanstack/react-query";
import {
    type QueryFunction,
    type QueryKey,
    useQueries,
    useQuery,
    useSuspenseQuery,
} from "@tanstack/react-query";
import type { GetResponse, GetSingleResponse } from "@towni/common";
import { emptyArrayOf, isFunction } from "@towni/common";
import type { Atom, Getter } from "jotai";
import { atomWithQuery } from "jotai-tanstack-query";
import { useMemo } from "react";
import type { Except } from "type-fest";
import { fetchClientAtom, useFetchClient } from "../fetch-client";
import { useSuspendedUntilEnabled } from "../suspense/use-suspended-until-enabled";

type TanstackQueryOptions<ResponseType> = Except<
    UseQueryOptions<ResponseType>,
    "queryFn" | "queryKey"
>;
type TanstackSuspenseQueryOptions<ResponseType> = Except<
    UseSuspenseQueryOptions<ResponseType>,
    "queryFn" | "queryKey"
> & {
    /** If disabled it will suspend until enabled */
    disabled?: boolean | QueryFunction<ResponseType>;
};
/**
 * Same as TanstackSuspenseQueryOptions except generic type is turned into an array
 * so you don't have to do it yourself, could be more readable when used together with
 * type of `useTanstackMultiSuspenseQuery` that does the same thing
 */
type TanstackMultiSuspenseQueryOptions<ResponseType> =
    TanstackSuspenseQueryOptions<ResponseType[]>;

/**
 * Helper hook that wraps the useQuery hook from Tanstack and
 * provides a custom query function that fetches a single item
 * from our api. It assumes and requires the api/server
 * to return a response in the `GetSingleResponse<ResponseType>` format.
 *
 * @param route url to fetch the data from
 * @param queryKey queryKey of the query
 * @param options additional options for the query, corresponds to tanstack query's useQuery options
 * @returns
 */
const useTanstackSingleQuery = <ResponseType extends Record<string, unknown>>(
    route: string,
    queryKey: QueryKey,
    options?: TanstackQueryOptions<ResponseType>,
): UseQueryResult<ResponseType> => {
    const fetchClient = useFetchClient();
    const queryFn: QueryFunction<ResponseType> = async ({ signal }) => {
        const data = await fetchClient.get<GetSingleResponse<ResponseType>>({
            route,
            customConfig: {
                signal,
            },
        });
        return data?.item;
    };

    return useQuery<ResponseType>({
        queryKey,
        queryFn,
        ...options,
    });
};

/**
 * Helper hook that wraps the useQuery hook from Tanstack and
 * provides a custom query function that fetches a single item
 * from our api. It assumes and requires the api/server
 * to return a response in the `GetSingleResponse<ResponseType>` format.
 *
 * NOTE! Returns null if disabled or none found (404)
 *
 * @param route url to fetch the data from
 * @param queryKey queryKey of the query
 * @param options additional options for the query, corresponds to tanstack query's useQuery options
 * @returns
 */
const useTanstackSingleSuspenseQuery = <
    ResponseType extends Record<string, unknown>,
>(
    route: string,
    queryKey: QueryKey,
    options: TanstackSuspenseQueryOptions<ResponseType> = {},
): UseSuspenseQueryResult<ResponseType> => {
    const fetchClient = useFetchClient();
    const { disabled = false, ...passOnOptions } = options;
    const _enabled = disabled === false || isFunction(disabled);
    useSuspendedUntilEnabled(_enabled, route);
    console.log("useTanstackSingleSuspenseQuery", {
        route,
        queryKey,
        disabled,
    });
    const queryFn: QueryFunction<ResponseType> = isFunction(disabled)
        ? disabled
        : async ({ signal }) => {
              const data = await fetchClient.get<
                  GetSingleResponse<ResponseType>
              >({
                  route,
                  customConfig: {
                      signal,
                  },
              });
              return data.item;
          };

    return useSuspenseQuery<ResponseType>({
        queryKey,
        queryFn,
        ...passOnOptions,
    });
};

/**
 * Helper hook that wraps the useQuery hook from Tanstack and
 * provides a custom query function that fetches an array of items
 * from our api. It assumes and requires the api/server
 * to return a response in the `GetResponse<ResponseType>` format
 * @param route url to fetch the data from
 * @param queryKey queryKey of the query
 * @param options additional options for the query, corresponds to tanstack query's useQuery options
 * @returns
 */
const useTanstackMultiQuery = <ResponseType extends Record<string, unknown>>(
    route: string,
    queryKey: QueryKey,
    options?: TanstackQueryOptions<ResponseType[]>,
): UseQueryResult<ResponseType[]> => {
    const fetchClient = useFetchClient();
    const queryFn: QueryFunction<ResponseType[]> = async ({
        signal,
    }): Promise<ResponseType[]> => {
        const data = await fetchClient.get<GetResponse<ResponseType>>({
            route,
            customConfig: {
                signal,
            },
        });
        return data?.items ?? emptyArrayOf<ResponseType>();
    };

    return useQuery<ResponseType[]>({
        queryKey,
        queryFn,
        ...options,
    });
};
/**
 * Helper hook that wraps the useQuery hook from Tanstack and
 * provides a custom query function that fetches an array of items
 * from our api. It assumes and requires the api/server
 * to return a response in the `GetResponse<ResponseType>` format
 * @param route url to fetch the data from
 * @param queryKey queryKey of the query
 * @param options additional options for the query, corresponds to tanstack query's useQuery options
 * @returns
 */
const useTanstackMultiSuspenseQuery = <
    ResponseType extends Record<string, unknown>,
>(
    route: string,
    queryKey: QueryKey,
    options: TanstackMultiSuspenseQueryOptions<ResponseType> = {},
): UseSuspenseQueryResult<ResponseType[]> => {
    const fetchClient = useFetchClient();
    const { disabled = false, ...passOnOptions } = options;
    const _enabled = disabled === false || isFunction(disabled);
    useSuspendedUntilEnabled(_enabled, route);

    const queryFn: QueryFunction<ResponseType[]> = isFunction(disabled)
        ? disabled
        : async ({ signal }): Promise<ResponseType[]> => {
              const data = await fetchClient.get<GetResponse<ResponseType>>({
                  route,
                  customConfig: {
                      signal,
                  },
              });
              return data.items ?? emptyArrayOf<ResponseType>();
          };

    return useSuspenseQuery<ResponseType[]>({
        queryKey,
        queryFn,
        ...passOnOptions,
    });
};
/**
 * Helper hook that wraps the useQuery hook from Tanstack and
 * provides a custom query function that fetches an array of items
 * from our api. It assumes and requires the api/server
 * to return a response in the `GetResponse<ResponseType>` format
 * @param route url to fetch the data from
 * @param queryKey queryKey of the query
 * @param options additional options for the query, corresponds to tanstack query's useQuery options
 * @returns
 */
const useTanstackMultiPostQuery = <
    Command extends Record<string, unknown>,
    ResponseType extends Record<string, unknown>,
>(
    route: string,
    queryKey: QueryKey,
    body: Command,
    options?: TanstackQueryOptions<ResponseType[]>,
): UseQueryResult<ResponseType[]> => {
    const fetchClient = useFetchClient();
    const queryFn: QueryFunction<ResponseType[]> = async ({
        signal,
    }): Promise<ResponseType[]> => {
        const data = await fetchClient.post<Command, GetResponse<ResponseType>>(
            {
                route,
                body,
                customConfig: {
                    signal,
                },
            },
        );
        return data?.items ?? emptyArrayOf<ResponseType>();
    };

    return useQuery<ResponseType[]>({
        queryKey,
        queryFn,
        ...options,
    });
};

const tanstackMultiQueryAtom = <ResponseType extends Record<string, unknown>>(
    getData: (get: Getter) => {
        route: string;
        queryKey: QueryKey;
        options?: TanstackQueryOptions<ResponseType[]>;
    },
): Atom<UseQueryResult<ResponseType[]>> => {
    return atomWithQuery<ResponseType[]>(get => {
        const { route, queryKey, options } = getData(get);
        const fetchClient = get(fetchClientAtom);
        return {
            queryKey,
            queryFn: async ({ signal }): Promise<ResponseType[]> => {
                const data = await fetchClient.get<GetResponse<ResponseType>>({
                    route,
                    customConfig: {
                        signal,
                    },
                });
                return data?.items ?? emptyArrayOf<ResponseType>();
            },
            ...options,
            initialData: emptyArrayOf<ResponseType>(),
        };
    });
};
const useTanstackMultiQueryAtom = <
    ResponseType extends Record<string, unknown>,
>(
    route: string,
    queryKey: QueryKey,
    options?: TanstackQueryOptions<ResponseType[]>,
): Atom<UseQueryResult<ResponseType[]>> => {
    const tanstackQueryAtom = useMemo(
        () =>
            tanstackMultiQueryAtom(_ => ({
                route,
                queryKey,
                options,
            })),
        [options, queryKey, route],
    );

    return tanstackQueryAtom;
};

/**
 * Helper hook that wraps the useQuery hook from Tanstack and
 * provides a custom query function that fetches `ResponseType`
 * from our api. To be used when the api doesn't return any of the default
 * `GetResponse<ResponseType>` or `GetSingleResponse<ResponseType>` formats.
 * @param route url to fetch the data from
 * @param queryKey queryKey of the query
 * @param options additional options for the query, corresponds to tanstack query's useQuery options
 * @returns
 */
const useTanstackCustomQuery = <ResponseType>(
    route: string,
    queryKey: QueryKey,
    options?: TanstackQueryOptions<ResponseType>,
): UseQueryResult<ResponseType> => {
    const fetchClient = useFetchClient();
    const queryFn: QueryFunction<ResponseType> = async ({ signal }) => {
        const data = await fetchClient.get<ResponseType>({
            route,
            customConfig: {
                signal,
            },
        });
        return data;
    };

    return useQuery<ResponseType>({
        queryKey,
        queryFn,
        ...options,
    });
};

/**
 * Helper hook that wraps the useQuery hook from Tanstack and
 * provides a custom query function that fetches `ResponseType`
 * from our api. To be used when the api doesn't return any of the default
 * `GetResponse<ResponseType>` or `GetSingleResponse<ResponseType>` formats.
 * @param route url to fetch the data from
 * @param queryKey queryKey of the query
 * @param options additional options for the query, corresponds to tanstack query's useQuery options
 * @returns
 */
const useTanstackCustomSuspenseQuery = <ResponseType>(
    route: string,
    queryKey: QueryKey,
    options?: TanstackSuspenseQueryOptions<ResponseType>,
): UseSuspenseQueryResult<ResponseType> => {
    const fetchClient = useFetchClient();
    const queryFn: QueryFunction<ResponseType> = async ({ signal }) => {
        const data = await fetchClient.get<ResponseType>({
            route,
            customConfig: {
                signal,
            },
        });
        return data;
    };

    return useSuspenseQuery<ResponseType>({
        queryKey,
        queryFn,
        ...options,
    });
};

/**
 * Helper hook that wraps the useQuery hook from Tanstack and
 * provides a custom query function that fetches a single item
 * from our api. It assumes and requires the api/server
 * to return a response in the `GetSingleResponse<ResponseType>` format.
 *
 * @param route url to fetch the data from
 * @param queryKey queryKey of the query
 * @param options additional options for the query, corresponds to tanstack query's useQuery options
 * @returns
 */
const useTanstackSingleQueries = <ResponseType extends Record<string, unknown>>(
    queryData: {
        route: string;
        queryKey: QueryKey;
        options?: TanstackQueryOptions<ResponseType>;
    }[],
) => {
    type Queries = UseQueryOptions<ResponseType>[];
    // https://github.com/TanStack/query/discussions/3991#discussioncomment-3410292
    const fetchClient = useFetchClient();
    const queryOptions = queryData.map((qd): Queries[0] => {
        const { route, queryKey } = qd;
        const options: TanstackQueryOptions<ResponseType> =
            qd.options ?? ({} as TanstackQueryOptions<ResponseType>);
        const queryFn: QueryFunction<ResponseType> = async ({
            signal,
        }): Promise<ResponseType> => {
            const data = await fetchClient.get<GetSingleResponse<ResponseType>>(
                {
                    route,
                    customConfig: {
                        signal,
                    },
                },
            );
            return data?.item;
        };

        return {
            queryKey,
            queryFn,
            ...options,
        } satisfies Queries[0];
    });

    return useQueries<Queries>({
        queries: queryOptions as QueriesOptions<Queries>,
    });
};

const useTanstackMultiQueries = <ResponseType extends Record<string, unknown>>(
    queryData: {
        route: string;
        queryKey: QueryKey;
        options?: TanstackQueryOptions<ResponseType[]>;
    }[],
) => {
    type Queries = UseQueryOptions<ResponseType[]>[];
    // https://github.com/TanStack/query/discussions/3991#discussioncomment-3410292
    const fetchClient = useFetchClient();
    const queryOptions = queryData.map((qd): Queries[0] => {
        const { route, queryKey } = qd;
        const options: TanstackQueryOptions<ResponseType[]> =
            qd.options ?? ({} as TanstackQueryOptions<ResponseType[]>);
        const queryFn: QueryFunction<ResponseType[]> = async ({
            signal,
        }): Promise<ResponseType[]> => {
            const data = await fetchClient.get<GetResponse<ResponseType>>({
                route,
                customConfig: {
                    signal,
                },
            });
            return data?.items;
        };

        return {
            queryKey,
            queryFn,
            ...options,
        } satisfies Queries[0];
    });

    return useQueries<Queries>({
        queries: queryOptions as QueriesOptions<Queries>,
    });
};

const tanstack = {
    useTanstackSingleQuery,
    useTanstackSingleSuspenseQuery,
    useTanstackSingleQueries,
    useTanstackMultiQuery,
    useTanstackMultiSuspenseQuery,
    useTanstackMultiQueries,
    useTanstackMultiPostQuery,
    useTanstackCustomQuery,
};

export {
    tanstack,
    tanstackMultiQueryAtom,
    useTanstackCustomQuery,
    useTanstackCustomSuspenseQuery,
    useTanstackMultiPostQuery,
    useTanstackMultiQueries,
    useTanstackMultiQuery,
    useTanstackMultiQueryAtom,
    useTanstackMultiSuspenseQuery,
    useTanstackSingleQueries,
    useTanstackSingleQuery,
    useTanstackSingleSuspenseQuery,
};
export type {
    TanstackMultiSuspenseQueryOptions,
    TanstackQueryOptions,
    TanstackSuspenseQueryOptions,
};
